import { Link } from "react-router-dom";
import {
  ArrowLeft,
  BookBookmark,
  Books,
  Buildings,
  ChatCircleDots,
  Files,
  MagnifyingGlass,
  Scales,
  ShieldCheck,
} from "@phosphor-icons/react";
import LexiqLogo from "../components/LexiqLogo";
import LexiqWordmark from "../components/LexiqWordmark";
import MobileLanding from "../components/landing/MobileLanding";
import useIsMobile from "../hooks/useIsMobile";
import { prefetchCases } from "../lib/casesStore";
import { prefetchLaws } from "../lib/lawsStore";
import { prefetchRegulations } from "../lib/regulationsStore";
import { prefetchConversations } from "../lib/conversationsStore";

const PREFETCH_BY_ROUTE = {
  "/cases": prefetchCases,
  "/laws": prefetchLaws,
  "/internal-regulations": prefetchRegulations,
  "/messages": prefetchConversations,
};

const FEATURES = [
  { icon: MagnifyingGlass, title: "تحليل القضية", desc: "تصنيف القضية، نقاط القوة والضعف، احتمالية النجاح، والإجراءات الموصى بها.", href: "/analyze", cta: "ابدأ التحليل", testid: "feature-analyze" },
  { icon: Files, title: "صياغة الوثائق", desc: "شكاوى، عرائض دعوى، مذكرات دفاع، طعون تمييزية، عقود، وكالات، وإنذارات.", href: "/draft", cta: "صياغة وثيقة", testid: "feature-draft" },
  { icon: BookBookmark, title: "الأنظمة الداخلية", desc: "النظام الداخلي للمؤسسات الحكومية والنقابات والوزارات العراقية.", href: "/internal-regulations", cta: "تصفّح الأنظمة", testid: "feature-regulations" },
  { icon: Scales, title: "إدارة القضايا", desc: "أرشف قضاياك، راجع تحليلاتها، وتتبع المواعيد القانونية الحرجة.", href: "/cases", cta: "قضاياي", testid: "feature-cases" },
  { icon: Books, title: "مكتبة القوانين", desc: "قانون العقوبات، المدني، الأحوال الشخصية، العمل، الشركات، والدستور.", href: "/laws", cta: "تصفّح القوانين", testid: "feature-laws" },
  { icon: Buildings, title: "المنظومة القضائية", desc: "هرمية المحاكم العراقية: الاتحادية العليا، التمييز، الاستئناف، البداءة وغيرها.", href: "/courts", cta: "استكشف المحاكم", testid: "feature-courts" },
];

export default function Landing() {
  // Viewport-conditional render (added 2026-06-30): previously we
  // returned BOTH <MobileLanding/> + <DesktopLanding/> and let Tailwind
  // hide one with `lg:hidden`. That doubled React's reconciliation work,
  // ran every useEffect twice, and burnt iOS Safari memory. With
  // `useIsMobile()` only ONE of the two trees ever mounts. Visual
  // result is identical because we mirror the same `lg` (1024 px)
  // breakpoint the Tailwind classes were using.
  const isMobile = useIsMobile();
  return (
    <div dir="rtl">
      {isMobile ? <MobileLanding /> : <DesktopLanding />}
    </div>
  );
}

function DesktopLanding() {
  return (
    <div className="hidden lg:block marble-texture">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-16">
        <div className="grid grid-cols-12 gap-10 items-center">
          <div className="col-span-7 lex-fade">
            <div className="flex items-center gap-3 mb-6">
              <LexiqLogo size={56} />
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#8A8A8A] font-cairo">
                مساعد قانوني ذكي · جمهورية العراق
              </span>
            </div>
            <h1 className="font-amiri text-6xl lg:text-7xl font-bold text-[#0A192F] leading-[1.05]">
              <LexiqWordmark className="text-6xl lg:text-7xl" testid="landing-hero-lexiq" />
              <span className="block text-[#1A1A1A] text-4xl lg:text-5xl mt-3">
                ذكاءٌ قانوني، بمنطقِ القاضي ودقّةِ المحامي
              </span>
            </h1>
            <p className="mt-8 text-lg text-[#4A4A4A] leading-relaxed max-w-2xl">
              منصة متكاملة تُحلّل قضاياك وفق القانون العراقي، تصوغ وثائقك بلغة قانونية
              رصينة، وتُجيب على استفساراتك مع الاستشهاد بالمواد والقوانين والقرارات
              التمييزية ذات الصلة.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/analyze" data-testid="hero-cta-analyze" className="lex-btn-primary inline-flex items-center gap-2">
                <span>ابدأ بتحليل قضية</span><ArrowLeft size={18} weight="bold" />
              </Link>
              <Link to="/chat" data-testid="hero-cta-chat" className="lex-btn-secondary inline-flex items-center gap-2">
                <ChatCircleDots size={18} weight="duotone" /><span>اسأل سؤالاً قانونياً</span>
              </Link>
            </div>
          </div>

          <div className="col-span-5 lex-fade lex-delay-2">
            <div className="relative paper-ivory border border-[#D4AF37]/30 p-14 flex flex-col items-center text-center min-h-[420px]">
              <div className="absolute -inset-2 border border-[#D4AF37]/40 pointer-events-none" />
              <div className="mb-5 flex items-center gap-2">
                <ShieldCheck size={22} weight="duotone" color="#D4AF37" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#8A8A8A]">مبدأ قانوني</span>
              </div>
              <p className="font-amiri text-3xl lg:text-4xl text-[#0A192F] leading-[1.7]" data-testid="legal-principle-quote">
                «العَدلُ أَساسُ المُلك، والقَضاءُ حِصنُها الحَصين.»
              </p>
              <div className="mt-6 w-12 h-[2px] bg-[#D4AF37]" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FAF9F6] border-y border-[#EAEAEA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#8A8A8A] mb-3">القدرات</div>
              <h2 className="font-amiri text-4xl lg:text-5xl text-[#0A192F]">ست واجهات قانونية متكاملة</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#EAEAEA] border border-[#EAEAEA]">
            {FEATURES.map((f) => (
              <Link
                key={f.title}
                to={f.href}
                data-testid={f.testid}
                onPointerDown={PREFETCH_BY_ROUTE[f.href]}
                className="bg-white p-8 group hover:bg-[#FAF9F6] transition-colors flex flex-col"
              >
                <f.icon size={32} weight="duotone" color="#D4AF37" />
                <h3 className="font-amiri text-2xl text-[#0A192F] mt-5 mb-3">{f.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed flex-1">{f.desc}</p>
                <span className="mt-6 editorial-link text-sm font-bold text-[#0A192F] inline-flex items-center gap-1.5">
                  {f.cta} <ArrowLeft size={14} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
